import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { User } from '../_models/user';
import { AppService } from './app.service';
import {
  LOGIN_REFRESH_TOKEN,
  LOGIN_SERVICE,
  LOGOUT,
} from './service.constants';
import { GlobalDataService } from './global-data.service';
import { VaultService } from './vault.service';
import { USER_OBJECT } from './vault-service.constants';
import { ClearCacheService } from './clear-cache.service';
import { SharedService } from './SharedDataService';
import { loginPayload, loginResponse, userType } from '../interfaces/login';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  // Init with null to filter out the first value in a guard!
  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  public user: Observable<User>;
  private idleLogoutTimer: any;
  private userSubject: BehaviorSubject<User>;
  private refreshTokenTimeout: any;
  constructor(
    private router: Router,
    private apiService: AppService,
    public globalData: GlobalDataService,
    private vaultService: VaultService,
    private clearCache: ClearCacheService,
    private sharedService: SharedService,
    private http: HttpClient
  ) {
    this.userSubject = new BehaviorSubject<User>(new User());
    this.user = this.userSubject.asObservable();
  }

  public get userValue(): User {
    return this.userSubject.value;
  }

  async login(loginObject: loginPayload) {
    return new Promise(async (resolve, reject) => {
      await (
        await this.apiService.authenticate(LOGIN_SERVICE, loginObject)
      ).subscribe(
        data => {
          let responseObject: loginResponse | any = data;
          if (responseObject && responseObject.isMaintenance) {
            this.sharedService.setMaintenanceRes(responseObject);
            this.router.navigateByUrl('maintenance');
          }
          const user: userType | any = {
            firstName: '',
            lastName: '',
            authToken: '',
            refreshToken: '',
            caseInfo: '',
            userCases: '',
            allowedRoles: '',
            status: '',
            userType: '',
            irn: '',
          };
          user.firstName = responseObject.firstName;
          user.lastName = responseObject.lastName;
          user.password = loginObject.password || '';
          user.authToken = responseObject.accessToken;
          user.refreshToken = responseObject.refreshToken;
          user.caseInfo = responseObject.caseInfo;
          user.userCases = responseObject.userCases;
          user.irn = responseObject?.irn || null;
          user.allowedRoles = responseObject.allowedRoles;
          user.status = responseObject.status;
          user.userType = responseObject.userType;
          this.vaultService.setAuthToken(responseObject?.authToken);
          this.vaultService.setRefreshToken(responseObject?.refreshToken);
          this.vaultService.setKeyValue(USER_OBJECT, user);
          this.userSubject.next(user);
          this.isAuthenticated.next(true);
          this.globalData.showAppRatingAndroid = responseObject?.showAppRatingAndroid === "true" ? true : false;
          this.globalData.showAppRatingIos = responseObject?.showAppRatingIos === "true" ? true : false;
          this.globalData.userAgreementStatus = responseObject.agreementStatus;
          resolve(responseObject);
        },
        error => {
          this.isAuthenticated.next(false);
          reject(error);
        }
      );
    });
  }

  async logout() {
    await this.logoutServerSide();
  }
  clearUserObject() {
    const user = new User();
    this.userSubject.next(user);
  }

  async logoutServerSide() {
    const initUploadImageResponse$ = await this.analyticsTracker(LOGOUT);
  }
  private async analyticsTracker(url: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const resp$ = await this.apiService.getServiceWithoutToken(url);
        const responseObject: any = await lastValueFrom(resp$);
        await this.vaultService.setKeyValue(USER_OBJECT, '');
        await this.vaultService.clearVault();
        this.userSubject.next(new User());
        this.isAuthenticated.next(false);
        await this.clearCache.clearCacheForFile('index.html');
        this.router.navigate(['landing']);
        resolve(responseObject);
      } catch (error) {
        await this.vaultService.setKeyValue(USER_OBJECT, '');
        await this.vaultService.clearVault();
        this.userSubject.next(new User());
        this.isAuthenticated.next(false);
        await this.clearCache.clearCacheForFile('index.html');
        this.router.navigate(['landing']);
        reject(error);
      }
    });
  }

  async refreshToken() {
    let userObject = await this.vaultService.getKeyValue(USER_OBJECT);
    return new Promise(async (resolve, reject) => {
      await (
        await this.apiService.postTokenRefreshService(LOGIN_REFRESH_TOKEN, {
          refreshToken: userObject.refreshToken,
        })
      ).subscribe(
        async data => {
          const responseObject: loginResponse | any = data;

          let defaultUser: any = {};
          let user: any =
            (await this.vaultService.getKeyValue(USER_OBJECT)) ?? defaultUser;
          this.vaultService.setAuthToken(responseObject.accessToken);
          this.vaultService.setRefreshToken(responseObject.refreshToken);
          user.authToken = responseObject?.accessToken;
          user.refreshToken = responseObject?.refreshToken;
          this.vaultService.setKeyValue(USER_OBJECT, user);
          this.isAuthenticated.next(true);
          resolve(userObject);
        },
        error => {
          this.isAuthenticated.next(false);
          reject(error);
        }
      );
    });
  }
  private restartIdleLogoutTimer() {
    clearTimeout(this.idleLogoutTimer);
    this.idleLogoutTimer = setTimeout(() => {
      this.logout();
    }, 900000); // 15 minutes
  }

  // helper methods
  public async isAccessTokenActive() {
    let userObject = await this.vaultService.getKeyValue(USER_OBJECT);
    if (userObject == null || !userObject?.authToken) {
      return false;
    }

    const jwtToken = JSON.parse(atob(userObject?.authToken.split('.')[1]));
    const tokenExpiryDateTime: any = jwtToken.exp * 1000;

    if (tokenExpiryDateTime <= Date.now()) {
      return false;
    } else {
      return true;
    }
  }
  private startRefreshTokenTimer() {
    const tkn: any = this.userValue.jwtToken;
    const jwtToken = JSON.parse(atob(tkn?.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const tokenExpiryDateTime: any = jwtToken.exp * 1000;
    const tokenTimeout = tokenExpiryDateTime - Date.now();
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken(),
      tokenTimeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
